import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useUnsavedChanges } from "@/lib/unsavedChanges"
import { domSave, hasLeaveEntry, moduleKeyOfPath, registerLeave, requestLeave, unregisterLeave } from "@/lib/leaveRegistry"

const LeaveCtx = React.createContext(null)

/** iter445 — chaque Dialog s'inscrit au registre commun dès qu'une valeur est saisie ; ✕ / Annuler / Échap / clic dehors passent par la fenêtre commune. */
const Dialog = ({ onOpenChange, open, children, ...props }) => {
  const id = React.useId()
  const contentRef = React.useRef(null)
  const closeNow = React.useCallback(() => { unregisterLeave(id); onOpenChange?.(false) }, [id, onOpenChange])
  const handle = React.useCallback((o) => { if (o || !hasLeaveEntry(id)) { onOpenChange?.(o); return } requestLeave("page", closeNow) }, [id, onOpenChange, closeNow])
  React.useEffect(() => { if (open === false) unregisterLeave(id) }, [open, id])
  React.useEffect(() => () => unregisterLeave(id), [id])
  const ctx = React.useMemo(() => ({ id, contentRef, closeNow }), [id, closeNow])
  return <LeaveCtx.Provider value={ctx}><DialogPrimitive.Root open={open} onOpenChange={handle} {...props}>{children}</DialogPrimitive.Root></LeaveCtx.Provider>
}

const MODULE_BY_PATH = () => moduleKeyOfPath()
const genericSave = (id, el) => domSave(id, () => el)

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, onInput, leaveLabel, ...props }, ref) => {
  const [dirty, setDirty] = React.useState(false);
  const [hasHeader, setHasHeader] = React.useState(false);
  useUnsavedChanges(dirty);
  const leave = React.useContext(LeaveCtx);
  const fields = React.useRef(new Set());
  const setRefs = (node) => { if (leave) leave.contentRef.current = node; if (typeof ref === "function") ref(node); else if (ref) ref.current = node; };
  const track = (e) => {
    const t = e.target; const key = t?.getAttribute?.("data-testid") || t?.name || t?.id || "champ";
    fields.current.add(key);
    if (leave) {
      const el = leave.contentRef.current;
      const title = leaveLabel || el?.querySelector("h2, [id^='radix-'][id$='-title'], [data-dialog-title]")?.textContent?.trim() || "Saisie en cours";
      const n = fields.current.size;
      registerLeave(leave.id, { label: title.slice(0, 60), module: MODULE_BY_PATH(), state: n > 1 ? `${n} champs modifiés` : "saisie en cours", save: genericSave(leave.id, el), discard: leave.closeNow });
    }
  };
  const cancelCapture = (e) => {
    const b = e.target.closest?.("button"); if (!b || !leave || !hasLeaveEntry(leave.id) || b.dataset.leaveBypass) return;
    const tid = b.getAttribute("data-testid") || "";
    if (!(/^\s*(annuler|fermer)\s*$/i.test(b.textContent || "") || /cancel|close/i.test(tid))) return;
    e.preventDefault(); e.stopPropagation();
    requestLeave("page", () => { unregisterLeave(leave.id); b.dataset.leaveBypass = "1"; b.click(); delete b.dataset.leaveBypass; });
  };
  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={setRefs}
      onClickCapture={cancelCapture}
      onInput={(e) => { setDirty(true); track(e); onInput?.(e); }}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      <HeaderCtx.Provider value={setHasHeader}>
        {!hasHeader && <div className="flex items-center justify-end" style={{ gap: 8, marginBottom: -8 }} data-testid="dialog-close-row"><CloseX /></div>}
        {children}
      </HeaderCtx.Provider>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const HeaderCtx = React.createContext(null)
/** iter447 pt 5 — bouton ✕ commun : 28×28, rayon 8, bordure #33333B, icône x #9A9AA2, jamais en position absolue. */
const CloseX = () => (
  <DialogPrimitive.Close className="inline-flex items-center justify-center shrink-0 hover:bg-white/5" style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid #33333B", color: "#9A9AA2" }} data-testid="dialog-close">
    <X size={14} /><span className="sr-only">Fermer</span>
  </DialogPrimitive.Close>
)

/** En-tête = rangée flex : contenu (titre, description) à gauche, pastille éventuelle (`badge`) puis ✕ à droite, écart 8 px, centrés verticalement. */
const DialogHeader = ({ className, badge = null, children, ...props }) => {
  const setHasHeader = React.useContext(HeaderCtx)
  React.useLayoutEffect(() => { setHasHeader?.(true); return () => setHasHeader?.(false) }, [setHasHeader])
  return (
    <div className={cn("flex items-center", className)} style={{ gap: 8 }} {...props}>
      <div className="flex flex-col space-y-1.5 text-left min-w-0" style={{ flex: 1 }}>{children}</div>
      {badge}
      <CloseX />
    </div>
  )
}
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
